// SAM-683 — the one EN/ES mechanism of this app, shared by the public pages
// (publicEvents.tsx: /events, /pay/:token, public invitation) and the signed-in admin
// (admin.tsx). Plain dictionaries keyed by string, `{name}` placeholders, the choice kept
// in localStorage. Each area keeps its own preference key and default: the public pages
// default to English, the admin to the browser's language.

export type Lang = 'en' | 'es';

export const LANGS: readonly Lang[] = ['en', 'es'];

export const isLang = (v: unknown): v is Lang => v === 'en' || v === 'es';

/** `?lang=es` / `?lang=en` in the current URL, if any. */
export const langFromUrl = (): Lang | null => {
  try {
    const v = new URLSearchParams(window.location.search).get('lang');
    return isLang(v) ? v : null;
  } catch {
    return null;
  }
};

export const readStoredLang = (storageKey: string): Lang | null => {
  try {
    const v = window.localStorage.getItem(storageKey);
    return isLang(v) ? v : null;
  } catch {
    return null;
  }
};

export const writeStoredLang = (storageKey: string, lang: Lang): void => {
  try {
    window.localStorage.setItem(storageKey, lang);
  } catch {
    /* private mode / storage blocked: the choice lasts for this page view only */
  }
};

/**
 * First of the browser's preferred languages that we have: `es-DO` → 'es', `en-US` → 'en'.
 * Anything else (or no navigator) falls back to `fallback`.
 */
export const browserLang = (fallback: Lang = 'en'): Lang => {
  try {
    const prefs = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const p of prefs) {
      const base = String(p || '').toLowerCase().split('-')[0];
      if (isLang(base)) return base;
    }
  } catch {
    /* no navigator */
  }
  return fallback;
};

/** Replace `{name}` placeholders. Values are inserted as text, never parsed. */
export const interpolate = (text: string, vars?: Record<string, string | number>): string => {
  if (!vars) return text;
  let out = text;
  for (const [name, value] of Object.entries(vars)) out = out.split(`{${name}}`).join(String(value));
  return out;
};

/**
 * Pick the active language from a value the backend already localizes (`{ en, es }`), falling
 * back to the other language, then to a plain string. Product names and other backend copy go
 * through this — never through the dictionaries.
 */
export const pickLang = (
  value: { en?: string | null; es?: string | null } | string | null | undefined,
  lang: Lang,
): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  const primary = value[lang];
  if (primary != null && String(primary).trim() !== '') return String(primary);
  const other = value[lang === 'en' ? 'es' : 'en'];
  return other != null ? String(other) : '';
};
